/**
 * Shared Chat Service Base
 *
 * Handles the common initialization steps for both PowerPoint and Excel chat services.
 * Host-specific services (PPT's taskpaneChatService, Excel's excelAgentMessageService)
 * call this base and then add their host-specific logic.
 *
 * Shared steps:
 * 1. Auth reload + JWT refresh
 * 2. GlobalStateManager.initialize(jwt)
 * 3. PromptService initialization
 * 4. User interview event listener registration
 *
 * Host-specific (provided via options):
 * - Document ID resolution (getOrCreatedocumentId / getOrCreateWorkbookId)
 * - Tracker startup (LiveTracker / ExcelTracker)
 * - Registry initialization (DeckRegistry / WorkbookRegistry)
 * - Host-specific event listeners (slide ops for PPT)
 */
import { authService } from "./authService";
import { getRuntimeGlobalState } from "./conversationRuntimeService";
import { setMessageService } from "../hooks/useSendMessage";
/**
 * Resolve an access token from auth storage/refresh flow.
 * Throws when no token is available after refresh attempts.
 */
export async function resolveJwtTokenOrThrow(errorMessage) {
    await authService.reloadFromStorage();
    let jwtToken = authService.getAccessToken();
    if (!jwtToken && authService.isAuthenticated()) {
        try {
            jwtToken = await authService.refreshAccessToken();
        }
        catch (error) {
            console.error("[chatServiceBase] Token refresh failed:", error);
        }
    }
    if (!jwtToken) {
        throw new Error(errorMessage || "JWT token required. Please ensure you are authenticated.");
    }
    return jwtToken;
}
/**
 * Initialize the shared chat service base.
 * Returns the JWT token, document ID, and cleanup functions.
 * Host services should call this first, then add their host-specific steps.
 */
export async function initializeChatServiceBase(options) {
    const { serviceName } = options;
    const initStartedAt = Date.now();
    console.log(`[${serviceName}] Initializing shared base...`);
    // 1. Auth reload + JWT refresh
    const jwtToken = await resolveJwtTokenOrThrow("JWT token required. Please ensure you are authenticated.");
    // 2. GlobalStateManager.initialize(jwt)
    try {
        const globalState = getRuntimeGlobalState();
        await globalState.initialize(jwtToken);
        console.log(`[${serviceName}] GlobalStateManager initialized`);
    }
    catch (error) {
        console.warn(`[${serviceName}] GlobalStateManager initialization failed:`, error);
    }
    // 3. Auth integration (if provided, PPT-specific)
    if (options.initializeAuthIntegration) {
        try {
            await options.initializeAuthIntegration();
        }
        catch (error) {
            console.warn(`[${serviceName}] Auth integration init failed:`, error);
        }
    }
    // 4. PromptService initialization (PPT-only)
    // Shared bootstrap intentionally does not import/initialize prompt service.
    // Non-PPT hosts should proceed with code fallbacks.
    // 5. Resolve document ID (host-specific)
    let documentId = null;
    if (options.resolveDocumentId) {
        try {
            documentId = await options.resolveDocumentId();
            console.log(`[${serviceName}] Document ID:`, documentId);
        }
        catch (error) {
            console.warn(`[${serviceName}] Could not resolve document ID:`, error);
        }
    }
    // 6. Register shared interview event listeners
    const { UserInterviewTool } = require("@carousel-office/agent-runtime/tools/userInterview");
    const handleInterviewSubmit = (event) => {
        const customEvent = event;
        console.log(`[${serviceName}] Interview submit event received:`, customEvent.detail);
        UserInterviewTool.submitAnswers(customEvent.detail.answers);
    };
    window.addEventListener("carousel:interview-submit", handleInterviewSubmit);
    const handleAgentInterviewSubmit = (event) => {
        const customEvent = event;
        console.log(`[${serviceName}] Agent interview submit event received:`, customEvent.detail);
        UserInterviewTool.submitAnswers(customEvent.detail.answers);
    };
    window.addEventListener("carousel:agent-interview-submit", handleAgentInterviewSubmit);
    const interviewCleanup = () => {
        window.removeEventListener("carousel:interview-submit", handleInterviewSubmit);
        window.removeEventListener("carousel:agent-interview-submit", handleAgentInterviewSubmit);
    };
    // 7. Register host-specific event listeners
    if (options.registerHostEventListeners) {
        options.registerHostEventListeners();
    }
    const coreReadyMs = Date.now() - initStartedAt;
    console.log(`[${serviceName}] Core initialization ready in ${coreReadyMs}ms`);
    // 8. Background bootstrap (non-blocking)
    void (async () => {
        const backgroundStartedAt = Date.now();
        try {
            if (options.initializeRegistry) {
                try {
                    await options.initializeRegistry();
                    console.log(`[${serviceName}] Registry initialized (background)`);
                }
                catch (error) {
                    console.warn(`[${serviceName}] Registry initialization failed (background):`, error);
                }
            }
            if (options.startTracking) {
                try {
                    await options.startTracking();
                    console.log(`[${serviceName}] Tracker started (background)`);
                }
                catch (error) {
                    console.warn(`[${serviceName}] Could not start tracker (background):`, error);
                }
            }
        }
        finally {
            const backgroundBootstrapMs = Date.now() - backgroundStartedAt;
            console.log(`[${serviceName}] Background bootstrap finished in ${backgroundBootstrapMs}ms`);
        }
    })();
    return { jwtToken, documentId, interviewCleanup };
}
/**
 * Shared runtime wrapper for host MessageService registration.
 * Hosts provide bootstrapping + service adapter construction.
 */
export function createLiveChatMessageService(options) {
    let registered = false;
    let registrationPromise = null;
    const initialize = async () => {
        if (registered) {
            return;
        }
        if (registrationPromise) {
            await registrationPromise;
            return;
        }
        registrationPromise = (async () => {
            if (options.ensureBootstrapped) {
                await options.ensureBootstrapped();
            }
            const service = await options.buildService();
            setMessageService(service);
            registered = true;
            console.log(`[${options.serviceName}] Message service registered`);
        })();
        try {
            await registrationPromise;
        }
        finally {
            if (!registered) {
                registrationPromise = null;
            }
        }
    };
    return {
        initialize,
        isInitialized: () => registered,
        reset: () => {
            registered = false;
            registrationPromise = null;
        },
    };
}
